import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { ThemeContext } from 'styled-components'

import { applyTextStyle } from '../../styles/typography'
import { fastTransition, shadows } from '../../styles/effects'
import { Icon } from '../icon'

export interface FilterChipDescriptor {
  key: string
  label: string
  onRemove?: () => void
}

interface IProps {
  chips: FilterChipDescriptor[]
  countLabel?: string
  onClearAll?: () => void
  clearAllLabel?: string
  className?: string
}

const FilterChips: React.FC<IProps> = ({
  chips,
  countLabel,
  onClearAll,
  clearAllLabel,
  className,
}) => {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()
  if (chips.length === 0 && !countLabel) return null

  const resolvedClearAllLabel = clearAllLabel ?? t('general.forms.clearFilter')

  return (
    <Wrapper className={className}>
      <Inner>
        {countLabel && <CountLabel>{countLabel}</CountLabel>}
        {chips.map((chip) =>
          chip.onRemove ? (
            <Chip
              key={chip.key}
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
            >
              <ChipLabel>{chip.label}</ChipLabel>
              <Icon
                type="Close"
                size={8}
                color={theme.colors.primaryFadeHalf}
              />
            </Chip>
          ) : (
            <StaticChip key={chip.key}>
              <ChipLabel>{chip.label}</ChipLabel>
            </StaticChip>
          )
        )}
      </Inner>
      {onClearAll && chips.length > 0 && (
        <ClearAllLink type="button" onClick={onClearAll}>
          {resolvedClearAllLabel}
        </ClearAllLink>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Inner = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const CountLabel = styled.span`
  ${applyTextStyle('caption')}
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.colors.text72};
  border-radius: 999px;
  background-color: transparent;
  cursor: pointer;
  transition: background-color ${fastTransition}, box-shadow ${fastTransition};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
    box-shadow: ${shadows.depthTwo};
  }
`

const StaticChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.colors.text72};
  border-radius: 999px;
  background-color: transparent;
`

const ChipLabel = styled.span`
  ${applyTextStyle('caption')}
  color: ${({ theme }) => theme.colors.textDark};
  white-space: nowrap;
`

const ClearAllLink = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  ${applyTextStyle('labelHeavy')}
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

export default FilterChips
